import React, { useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { reduxForm, InjectedFormProps, Field } from 'redux-form';
import moment, { Moment } from 'moment';
import * as R from 'ramda';
import { pick } from 'lodash';

import * as predicateUtil from '@common/utils/predicates';
import * as AlertService from '@common/services/alert';
import Container from '@common/components/container';
import MarkupEditor from '@common/components/form/inputs/markup/editor';
import { TopNavigationBar } from '@common/components/navigation-bar/top-navigation-bar/top-navigation-bar';
import { Button } from '@common/components/button';
import Collapsible from '@common/components/collapsible';
import CollapsibleGroupComponent from '@common/components/collapsible/group';
import {
  Row,
  Group,
  Label,
  TextInput,
  DatePickerInput,
  CheckboxInput,
} from '@common/components/form';
import { LocationInput } from '@common/components/form/inputs/location';
import CheckboxReverse from '@common/components/form/inputs/checkbox-reverse/checkbox-reverse';
import { AudienceInput } from '@common/components/form/audience';
import NumberInput from '@common/components/form/inputs/number';
import TimePickerInput from '@common/components/form/inputs/time';
import FileInput from '@common/components/form/inputs/file';
import ImagePreview from '@common/components/file-preview/image';
import { isEqualWithFlatDiff } from '@utils/isEqualWithFlatDiff';
import { useFormValues } from '@common/hooks/form-hook';
import { useThunkDispatch } from '@common/hooks';
import type { Attachment, FileInProgress } from '@common/types/objects';
import * as eventActions from '../../actions';
import { EEventStatus, EEventType } from '../../definitions';
import type { EventForAdminDto } from '../../types';
import { SurveyTypeSelector } from '@modules/survey/containers/publish-survey';
import { useQueryParam } from '@common/hooks/url';
import createTempId from '@utils/create-temp-id';
import {
  dateAndTimeToDate,
  dateAndTimeToStr,
  getMinTimeValue,
} from '@common/utils/date';
import Tooltip from '@common/components/tooltip';
import Icon from '@common/components/icon';
import NotificationsInput, {
  NotificationType,
} from '@modules/events/forms/event/notifications-input';
import { DAY_IN_MINUTES, HOUR_IN_MINUTES } from '@common/definitions';

export const DEFAULT_SEND_EVENT_START_REMINDER = [
  {
    id: createTempId(),
    value: '30',
    unit: 'minutes',
  },
];

const REQUIRED_FIELDS = {
  step1: ['start_date', 'name'],
  step3: [
    'settings.send_event_start_reminder',
    'settings.registration_deadline_date',
    'settings.registration_deadline_time',
    'settings.max_guest_limit',
  ],
};

const formatIntervalEntries = (entries: NotificationType[]) => {
  return entries
    .filter((entry) => {
      const n = parseInt(entry.value);
      return n === n; // NaN check
    })
    .map((entry) => {
      const n = parseInt(entry.value);
      if (entry.unit === 'hours') return n * HOUR_IN_MINUTES;
      if (entry.unit === 'days') return n * DAY_IN_MINUTES;
      return n;
    });
};

type FormState = {
  start_date: Moment;
  invite_type: 'direct' | 'ongoing';
  start_time: string;
  end_date: Moment;
  end_time: string;
  name: string;
  location: Record<string, string>;
  web_link: string;
  description: string;
  header_image: (Attachment & FileInProgress) | null;
  audience: Record<string, string>;
  has_deadline: boolean;
  is_full_day: boolean;
  unlimited_spots: boolean;
  status: string;
  settings: {
    disable_guest_list: boolean;
    registration_deadline_date: Moment;
    registration_deadline_time: string;
    max_guest_limit: number;
    hide_guest_list: boolean;
    only_admins_can_post: boolean;
    send_event_created_notification: boolean;
    send_event_availability_reminder_notification: boolean;
    send_event_start_reminder_toggle: boolean;
    send_event_start_reminder: NotificationType[];
    allow_attendance_change_after_deadline: boolean;
  };
};

type Props = InjectedFormProps<FormState> & {
  event?: EventForAdminDto;
  handleSubmit: () => Promise<unknown>;
};

const UnconnectedEventForm = ({
  event,
  handleSubmit,
  invalid,
  change,
  untouch,
  submitting,
  initialValues,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const thunkDispatch = useThunkDispatch();
  const groupRef = useRef<CollapsibleGroupComponent>(null);
  const formValues = useFormValues<FormState>('event-form');
  const now = moment();

  const { is_full_day } = formValues;
  useEffect(() => {
    if (is_full_day) {
      change('start_time', '08:00');
      change('end_time', '18:00');
    }
  }, [change, is_full_day]);

  useEffect(() => {
    // Adjust end time when start time gets changed to something after the end time
    const startTime = dateAndTimeToDate(
      formValues.start_time,
      formValues.start_date
    );
    const endTime =
      formValues.end_date &&
      formValues.end_time &&
      dateAndTimeToDate(formValues.end_time, formValues.end_date);

    if (endTime && startTime > endTime) {
      const newEndTime = startTime.clone().add(1, 'hour');

      change('end_date', newEndTime);
      change('end_time', newEndTime.format('HH:mm'));
    }
  }, [
    formValues.start_date,
    formValues.start_time,
    formValues.end_date,
    formValues.end_time,
  ]);

  useEffect(() => {
    // Adjust registration deadline when the start time is before the deadline
    const startTime = dateAndTimeToDate(
      formValues.start_time,
      formValues.start_date
    );
    const deadline =
      formValues.has_deadline &&
      formValues.settings.registration_deadline_date &&
      formValues.settings.registration_deadline_time &&
      dateAndTimeToDate(
        formValues.settings.registration_deadline_time,
        formValues.settings.registration_deadline_date
      );

    if (deadline && startTime < deadline) {
      const newDeadline = startTime.clone().subtract(1, 'day');

      change('settings.registration_deadline_date', newDeadline);
    }
  }, [
    formValues.start_date,
    formValues.start_time,
    formValues.has_deadline,
    formValues.settings.registration_deadline_date,
    formValues.settings.registration_deadline_time,
  ]);

  const parseStateToPayload = (values: FormState) => ({
    start_time: dateAndTimeToStr(values.start_time, values.start_date),
    end_time: dateAndTimeToStr(values.end_time, values.end_date),
    location: values.location,
    is_full_day: values.is_full_day,
    name: values.name,
    web_link: values.web_link || null,
    description: values.description || null,
    header_image_url: values.header_image?.is_remote_file
      ? values.header_image?.path
      : values.header_image?.raw_path || null,
    is_header_image_remote_file: values.header_image?.is_remote_file || false,
    audience:
      values.audience &&
      R.evolve(
        {
          predicates: predicateUtil.predicatesToPayload,
        },
        values.audience
      ),
    settings: {
      disable_guest_list: values.settings.disable_guest_list,
      max_guest_limit: !values.unlimited_spots
        ? values.settings.max_guest_limit
        : null,
      hide_guest_list: values.settings.hide_guest_list,
      only_admins_can_post: values.settings.only_admins_can_post,
      send_event_created_notification:
        values.settings.send_event_created_notification,
      send_event_availability_reminder_notification:
        values.settings.send_event_availability_reminder_notification,
      send_event_start_reminder: values.settings
        .send_event_start_reminder_toggle
        ? formatIntervalEntries(values.settings.send_event_start_reminder)
        : [],
      registration_deadline: values.has_deadline
        ? dateAndTimeToStr(
          values.settings.registration_deadline_time,
          values.settings.registration_deadline_date
        )
        : null,
      ...(values.has_deadline === true && {
        allow_attendance_change_after_deadline:
          !!values.settings.allow_attendance_change_after_deadline,
      }),
    },
    status: values.status || undefined,
    invite_type: values.invite_type,
  });

  const submitFn = async (values: FormState) => {
    const payload = parseStateToPayload(values);

    const redirect = (status: string) => history.push(
      status === EEventStatus.DRAFT
        ? '/admin/events/filter/draft'
        : '/admin/events'
    );

    if (event) {
      try {
        const [isEqual, partialPayload] = isEqualWithFlatDiff(
          payload,
          // @ts-expect-error
          parseStateToPayload(initialValues)
        );

        let { status } = values;

        if (!isEqual && partialPayload) {
          partialPayload.start_time = payload.start_time;
          const { item } = await thunkDispatch(
            // @ts-expect-error
            eventActions.updateEvent(event.id, partialPayload)
          );
          status = item.status;
          AlertService.success(t('events:updated_event'));
          redirect(status);
        } else {
          AlertService.warning(t('core:no_changes'));
        }
      } catch (response) {
        // @ts-expect-error
        AlertService.forStatus(response.status, {
          warning: t('events:warning_updating_event'),
          error: t('events:error_updating_event'),
        });
      }
    } else {
      try {
        // @ts-expect-error
        const { item } = await thunkDispatch(eventActions.createEvent(payload));

        AlertService.success(t('events:created_event'));
        redirect(item.status);
      } catch (response) {
        // @ts-expect-error
        AlertService.forStatus(response.status, {
          warning: t('events:warning_creating_event'),
          error: t('events:error_creating_event'),
        });
      }
    }
  };

  // if we delete all the entries for the time intervals of the notifications then
  // turn off the toggle for the reminder intervals
  const { send_event_start_reminder, send_event_start_reminder_toggle } =
    formValues.settings;
  useEffect(() => {
    if (!send_event_start_reminder || send_event_start_reminder.length <= 0) {
      change('settings.send_event_start_reminder_toggle', false);
    }
  }, [send_event_start_reminder, change]);

  // if we turn on the toggle but do not have any entries, then create a default one
  useEffect(() => {
    if (
      send_event_start_reminder_toggle &&
      (!send_event_start_reminder || send_event_start_reminder.length <= 0)
    ) {
      change(
        'settings.send_event_start_reminder',
        DEFAULT_SEND_EVENT_START_REMINDER
      );
    }
  }, [send_event_start_reminder, send_event_start_reminder_toggle, change]);

  const back = useQueryParam('back');
  const isLive = event?.status === EEventStatus.LIVE;

  return (
    <form onSubmit={handleSubmit(submitFn)} className="Form EventForm">
      <TopNavigationBar
        title={event ? event.name : t('events:create_event')}
        breadcrumbs={[
          {
            name: t('core:tab_events'),
            path: '/admin/events',
          },
          {
            name: event ? event.name : t('events:create_event'),
            mask: true,
          },
        ]}
        action={
          <>
            <Button onClick={() => history.push(back || '/admin/events')}>
              <Trans i18nKey="events:go_back" />
            </Button>
            {(!event || event.status === EEventStatus.DRAFT) && (
              <Button
                buttonType="submit"
                disabled={invalid}
                isLoading={submitting}
              >
                <Trans i18nKey="events:save_and_close" />
              </Button>
            )}
            {!event || event.status === EEventStatus.DRAFT ? (
              <Button
                buttonType="submit"
                onClick={() => change('status', 'LIVE')}
                type="primary"
                icon="send"
                disabled={invalid}
                isLoading={submitting}
              >
                <Trans i18nKey="events:create_event" />
              </Button>
            ) : (
              <Button
                buttonType="submit"
                type="primary"
                disabled={invalid}
                isLoading={submitting}
              >
                <Trans i18nKey="events:update_event" />
              </Button>
            )}
          </>
        }
      />
      <Container.Content>
        <Collapsible.Group ref={groupRef}>
          <Collapsible
            title={t('events:forms_event_details')}
            fields={REQUIRED_FIELDS.step1}
            {...pick(props, ['form', 'touch'])}
          >
            <Collapsible.Column flex={1}>
              <Row>
                <Group flex={4}>
                  <Label
                    forInput="start_date"
                    text={t('events:forms_event_start_date')}
                    required
                  />
                  <DatePickerInput name="start_date" minDate={now} />
                </Group>
                <Group flex={2}>
                  <Label
                    forInput="start_time"
                    text={t('events:forms_event_start_time')}
                  />
                  <TimePickerInput
                    name="start_time"
                    disabled={formValues.is_full_day}
                    minValue={getMinTimeValue(formValues.start_date)}
                  />
                </Group>
                <Group flex={1}>
                  <Label text={<span>&nbsp;</span>} />
                  <div style={{ padding: '9px 0' }}>
                    <CheckboxInput
                      name="is_full_day"
                      label={t('events:forms_event_full_day')}
                    />
                  </div>
                </Group>
              </Row>
              <Row>
                <Group flex={4}>
                  <Label
                    forInput="end_date"
                    text={t('events:forms_event_end_date')}
                  />
                  <DatePickerInput
                    name="end_date"
                    minDate={formValues.start_date}
                    placeholder={t('events:forms_event_select_date')}
                  />
                </Group>
                <Group flex={2}>
                  <Label
                    forInput="end_time"
                    text={t('events:forms_event_end_time')}
                  />
                  <TimePickerInput
                    name="end_time"
                    disabled={formValues.is_full_day}
                    minValue={
                      formValues.start_date &&
                        formValues.start_date.isSame(formValues.end_date, 'day')
                        ? formValues.start_time
                        : undefined
                    }
                    placeholder={t('events:forms_event_select_time')}
                  />
                </Group>
                <Group flex={1} />
              </Row>
              <Row>
                <Group>
                  <Label
                    forInput="name"
                    text={t('events:forms_event_name')}
                    maxLength={100}
                    required
                  />
                  <TextInput
                    name="name"
                    placeholder={t('events:forms_event_name_placeholder')}
                    maxLength={100}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label
                    forInput="location"
                    text={t('events:forms_event_location')}
                  />
                  <LocationInput
                    initialValue={formValues.location}
                    onChange={(location) => change('location', location)}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label
                    forInput="web_link"
                    text={t('events:forms_event_web_link')}
                  />
                  <TextInput name="web_link" placeholder="https://" />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label
                    forInput="description"
                    text={t('events:forms_event_description')}
                  />
                  <MarkupEditor
                    value={event?.description || ''}
                    onChange={(value: string) => change('description', value)}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <Label text={t('events:forms_event_cover_image')} />
                  {!formValues.header_image?.path ? (
                    <FileInput
                      unsplash
                      name="header_image"
                      accept="image/*"
                      acceptedExtLabel="JPG, PNG, GIF"
                    />
                  ) : (
                    <div className="EventForm__HeaderImage">
                      <ImagePreview
                        onRemove={() => change('header_image', null)}
                        file={formValues.header_image}
                        size={undefined}
                        showImageOnHover
                      />
                    </div>
                  )}
                </Group>
              </Row>
            </Collapsible.Column>
          </Collapsible>

          {event?.status === EEventStatus.LIVE &&
            event.invite_type === EEventType.DIRECT ? null : (
              <Collapsible title={t('events:forms_event_audience')}>
                <Collapsible.Column flex={1}>
                  {event?.status !== EEventStatus.LIVE && (
                  <Row>
                    <Group>
                      <Label text={t('survey:publish_form_type')} />
                      <Field
                        name="invite_type"
                        component={SurveyTypeSelector}
                      />
                    </Group>
                  </Row>
                )}
                  <AudienceInput name="audience" />
                </Collapsible.Column>
              </Collapsible>
          )}

          <Collapsible
            title={t('events:forms_event_settings')}
            fields={REQUIRED_FIELDS.step3}
            {...pick(props, ['form', 'touch'])}
          >
            <Collapsible.Column flex={1}>
              <Row>
                <h4>{t('events:forms_event_guest_list')}</h4>
              </Row>
              <Row>
                <Group>
                  <CheckboxInput
                    name="settings.disable_guest_list"
                    label={t('events:forms_event_disable_guest_list')}
                  />
                </Group>
              </Row>
              {!formValues.settings.disable_guest_list && (
                <Row>
                  <Group>
                    <CheckboxInput
                      name="settings.hide_guest_list"
                      label={t('events:forms_event_hide_guest_list')}
                    />
                  </Group>
                </Row>
              )}
              <hr />

              {!formValues.settings.disable_guest_list && (
                <>
                  <Row>
                    <h4>{t('events:forms_event_spots')}</h4>
                  </Row>
                  <Row>
                    <Group>
                      <CheckboxInput
                        name="unlimited_spots"
                        label={t('events:forms_event_unlimited_spots')}
                      />
                    </Group>
                  </Row>
                  <Row>
                    <Group flex={6}>
                      <CheckboxReverse
                        name="unlimited_spots"
                        label={t('events:forms_event_limited_spots')}
                      />
                    </Group>
                    {!formValues.unlimited_spots && (
                      <>
                        <Group
                          style={{ textAlign: 'right', lineHeight: '34px' }}
                        >
                          {t('events:forms_event_set_number_of_spots')}
                        </Group>
                        <Group flex={2}>
                          <NumberInput
                            name="settings.max_guest_limit"
                            min={0}
                            placeholder={t('events:forms_event_set_amount')}
                          />
                        </Group>
                      </>
                    )}
                  </Row>
                  <hr />
                </>
              )}

              <Row>
                <h4>{t('events:forms_event_posting')}</h4>
              </Row>
              <Row>
                <Group>
                  <CheckboxInput
                    name="settings.only_admins_can_post"
                    label={t('events:forms_event_only_admins_can_post')}
                  />
                </Group>
              </Row>
              <hr />

              <Row>
                <h4>{t('events:forms_event_notifications')}</h4>
              </Row>
              <Row>
                <Group>
                  <CheckboxInput
                    name="settings.send_event_created_notification"
                    label={t('events:forms_event_should_notify_employees')}
                    disabled={isLive}
                  />
                </Group>
              </Row>
              <Row>
                <Group>
                  <CheckboxInput
                    name="settings.send_event_availability_reminder_notification"
                    label={
                      <>
                        {t('events:forms_event_remind_availability')}
                        <Tooltip
                          title={t(
                            'events:forms_event_remind_availability_tooltip'
                          )}
                        >
                          <Icon
                            type="info"
                            className="formsEventRemindAvailabilityIcon"
                          />
                        </Tooltip>
                      </>
                    }
                    disabled={isLive}
                  />
                </Group>
              </Row>
              <Row>
                <Group flex="0 0 auto" style={{ width: 'auto' }}>
                  <CheckboxInput
                    name="settings.send_event_start_reminder_toggle"
                    label={t('events:forms_events_alert_before_start')}
                    disabled={isLive}
                  />
                  {formValues.settings.send_event_start_reminder_toggle && (
                    <NotificationsInput
                      name="settings.send_event_start_reminder"
                      startDate={formValues.start_date}
                      startTime={formValues.start_time}
                      startIsFullDay={formValues.is_full_day}
                      disabled={isLive}
                    />
                  )}
                </Group>
              </Row>

              {!formValues.settings.disable_guest_list && (
                <>
                  <hr />
                  <Row>
                    <h4>{t('events:forms_event_deadline')}</h4>
                  </Row>
                  <Row>
                    <Group flex={3}>
                      <CheckboxInput
                        name="has_deadline"
                        label={t('events:forms_event_registration_deadline')}
                        onChange={(value) => {
                          change('settings.allow_attendance_change_after_deadline', value);
                          change('has_deadline', value);
                        }}
                      />
                    </Group>
                  </Row>
                  {formValues.has_deadline && (
                    <>
                      <Row style={{ width: 600 }}>
                        <Group>
                          <DatePickerInput
                            name="settings.registration_deadline_date"
                            minDate={now}
                            maxDate={formValues.start_date}
                            placeholder={t('events:forms_event_select_date')}
                          />
                        </Group>
                        <Group>
                          <TimePickerInput
                            name="settings.registration_deadline_time"
                            maxValue={
                              formValues.start_date &&
                                formValues.start_date.isSame(
                                  formValues.settings.registration_deadline_date,
                                  'day'
                                )
                                ? formValues.start_time
                                : undefined
                            }
                            placeholder={t('events:forms_event_select_time')}
                          />
                        </Group>
                      </Row>
                      <Row>
                        <Group flex={3}>
                          <CheckboxInput
                            name="settings.allow_attendance_change_after_deadline"
                            label={t(
                              'events:forms_event_allow_attendance_change'
                            )}
                          />
                        </Group>
                      </Row>
                    </>
                  )}
                </>
              )}
            </Collapsible.Column>
          </Collapsible>
        </Collapsible.Group>
      </Container.Content>
    </form>
  );
};

export const EventForm = reduxForm<FormState, Props>({
  form: 'event-form',
  validate: (values: FormState) => {
    const errors: Record<string, unknown> = {};
    if (!values.name) errors.name = 'events:validation_name_required';

    if (
      values.start_date &&
      values.start_date.valueOf() < new Date().getTime()
    ) {
      errors.start_date = 'events:validation_start_date_in_the_past';
    }

    if (values.settings?.send_event_start_reminder) {
      errors.settings = {
        ...(errors.settings || {}),
        send_event_start_reminder:
          values.settings?.send_event_start_reminder.map(({ value }) => ({
            value: value ? false : 'events:validation_notification_required',
          })),
      };
    }

    if (!values.unlimited_spots) {
      errors.settings = {
        ...(errors.settings || {}),
        max_guest_limit: values.settings?.max_guest_limit
          ? false
          : 'events:validation_guest_limit_required',
      };
    }

    if (values.has_deadline) {
      errors.settings = {
        ...(errors.settings || {}),
        registration_deadline_date: values.settings.registration_deadline_date
          ? false
          : 'events:validation_deadline_date_required',
        registration_deadline_time: values.settings.registration_deadline_time
          ? false
          : 'events:validation_deadline_time_required',
      };
    }

    return errors;
  },
})(UnconnectedEventForm);
